import React from "react";
import { cn } from "@/lib/utils";
import { MouseEvent } from "react";
import { Link } from "lucide-react";

export interface FixtureMarkerProps {
  id: string;
  x: number | string;
  y: number | string;
  size: "small" | "medium" | "large";
  opacity?: number;
  isSelected?: boolean;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  circuitColor?: string;
  showConfidenceLabel?: boolean;
  confidenceScore?: number;
  isCircuitMode?: boolean;
  hasCircuit?: boolean;
  isInSelectedCircuit?: boolean;
  blinkingStyle?: React.CSSProperties;
  isLinkedCircuit?: boolean;
}

export function FixtureMarker({
  id,
  x,
  y,
  size = "medium",
  opacity = 1,
  isSelected = false,
  onClick,
  circuitColor,
  showConfidenceLabel = false,
  confidenceScore,
  isCircuitMode = false,
  hasCircuit = false,
  isInSelectedCircuit = false,
  blinkingStyle,
  isLinkedCircuit = false
}: FixtureMarkerProps) {
  // Size variants
  const sizeClasses = {
    small: "w-4 h-4",
    medium: "w-6 h-6",
    large: "w-8 h-8"
  };

  // Color states
  const getMarkerColor = () => {
    if (isCircuitMode && isSelected) {
      return "bg-yellow-500"; // Bright yellow for selected fixtures in circuit mode
    }
    
    if (!isCircuitMode && isSelected) {
      return "bg-primary"; // Primary theme color for selected fixtures in normal mode
    }
    
    if (hasCircuit) {
      return "bg-emerald-500"; // Always use emerald green for circuit-assigned fixtures
    }
    
    // Default color for unassigned/detected fixtures
    return "bg-blue-500";
  };
  
  const getBorderColor = () => {
    if (isSelected) {
      return isCircuitMode 
        ? "border-yellow-600 ring-2 ring-yellow-300" // Double border effect for circuit mode selection
        : "border-primary/50 ring-2 ring-primary"; // Double border effect for normal selection
    }
    // For circuit-assigned fixtures, use a darker emerald border
    if (hasCircuit) {
      return "border-emerald-700";
    }
    // Dark blue border for unassigned fixtures
    return "border-blue-700 hover:border-blue-800";
  };

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      e.stopPropagation();
      onClick(e);
    }
  };

  // Combine marker styles with blinking animation if marker is in selected circuit
  const combinedStyles = {
    left: typeof x === "string" ? x : `${x}px`,
    top: typeof y === "string" ? y : `${y}px`,
    zIndex: isSelected ? 10 : 5,
    opacity: opacity,
    transform: `translate(-50%, -50%) scale(${isSelected ? 1.2 : 1})`,
    backgroundColor: hasCircuit && circuitColor && circuitColor.toLowerCase() !== '#ffffff' 
      ? circuitColor 
      : undefined, // Let the Tailwind class handle the background color
    ...(isInSelectedCircuit ? blinkingStyle : {})
  };

  return (
    <div
      data-fixture-id={id}
      className={cn(
        "fixture-marker absolute -translate-x-1/2 -translate-y-1/2 rounded-full cursor-pointer transition-all duration-150",
        "border-2",
        sizeClasses[size],
        getBorderColor(),
        getMarkerColor(),
        isInSelectedCircuit ? "z-20" : ""
      )}
      style={combinedStyles}
      onClick={onClick ? handleClick : undefined}
      onMouseDown={onClick ? (e) => e.stopPropagation() : undefined}
    >
      {showConfidenceLabel && confidenceScore !== undefined && (
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] bg-black/80 text-white px-1 rounded whitespace-nowrap">
          {Math.round(confidenceScore * 100)}%
        </div>
      )}
      
      {isLinkedCircuit && (
        <div className="absolute -top-1 -right-1 bg-white rounded-full p-[2px] shadow-sm border border-blue-500">
          <Link className="h-3 w-3 text-blue-500" />
        </div>
      )}
    </div>
  );
}
